import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { OTP_LENGTH } from '../../constants';
import { ModalResult } from '../../interfaces/modal-result.interface';
import { CustomerService } from '../../services/customer.service';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import { OtpComponent } from '../otp/otp.component';
import { Api } from '@api/api';
import { confirmEmailStep1, confirmEmailStep2 } from '@api/functions';
import type { OtpConfirmDto, OtpRequestDto } from '@api/models';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';

@Component({
  selector: 'tc-confirm-email-modal',
  templateUrl: './confirm-email-modal.component.html',
  styleUrls: ['./confirm-email-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslatePipe,
    OtpComponent,
    IconComponent,
    ReactiveFormsModule,
    ButtonComponent,
  ],
})
export class ConfirmEmailModalComponent implements OnInit {
  protected dialogRef = inject(MatDialogRef<ConfirmEmailModalComponent, ModalResult>);
  protected customerService = inject(CustomerService);

  private destroyRef = inject(DestroyRef);
  private api = inject(Api);

  readonly otpDigitsFormArray = new FormArray(
    new Array(OTP_LENGTH).fill(null).map(() => new FormControl('', Validators.required)),
  );

  isLoading = signal<boolean>(false);
  error = signal<string>(null);
  state = signal<'title' | 'otp' | 'success'>('title');
  otpRequest = signal<OtpRequestDto>(null);

  ngOnInit(): void {
  }

  onSubmit(): void {
    this.error.set(null);

    switch (this.state()) {
      case 'title':
        this.confirmEmailInit();
        this.dialogRef.disableClose = true;
        break;

      case 'otp':
        this.isLoading.set(true);
        const otpConfirmDto: OtpConfirmDto = {
          requestId: this.otpRequest().requestId,
          otpCode: this.otpDigitsFormArray.getRawValue().join(''),
        };

        this.api.invoke(confirmEmailStep2, { body: otpConfirmDto })
          .pipe(
            finalize(() => this.isLoading.set(false)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: () => {
              this.state.set('success');
              this.dialogRef.disableClose = false;
            },
            error: error => this.error.set(resolveHttpErrorMessage(error)),
          });
        break;

      case 'success':
        this.dialogRef.close({ type: 'CONFIRM' });
        break;
    }
  }

  confirmEmailInit(): void {
    this.error.set(null);

    this.isLoading.set(true);

    this.api.invoke(confirmEmailStep1)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.otpRequest.set(response);
          this.state.set('otp');
        },
        error: error => this.error.set(resolveHttpErrorMessage(error)),
      });
  }
}
